'use client';

import { Carousel, CarouselApi, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import useIsMobile from '@/hooks/use-is-mobile';
import { cn } from '@/lib/utils';
import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import ColorsFamilyItem from './colors-family-item';
import { generateLinkHref } from './generate-link-href';

interface Aggregation {
  label: string;
  count: number;
  attribute_code: string;
  options: Option[];
  position?: number;
}

interface Option {
  label: string;
  value: string;
  swatch_data?: SwatchData;
  count: number;
}

interface SwatchData {
  value: string;
  thumbnail?: string;
}

interface AggregationRoot {
  code: string;
  option_id: string;
  rewrite: string;
  label?: string;
  swatch_data: SwatchData;
}

const ColorsFamilyFilter = ({
  colorsFamilyOptions,
  aggregations,
  categoryUrlKey,
  className,
  filterState,
  optionIdMapToRewrite,
}: {
  colorsFamilyOptions: NonNullable<
    NonNullable<NonNullable<NonNullable<GetCategoryFilterOptionsQuery['products']>['aggregations']>[0]>['options']
  >;
  aggregations: AggregationRoot[];
  optionIdMapToRewrite: Record<string, Record<string, string>>;
  categoryUrlKey: string;
  className?: string;
  filterState: FilterState;
}) => {
  const isMobile = useIsMobile();
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const prevStateIndex = filterState.findIndex(x => x.attributeCode === 'custom_option_color_ids');
  const urlColorRewrites = filterState[prevStateIndex]?.options?.map(x => x.rewrite) || [];
  const colorValueMapToAggregation = useMemo(
    () =>
      aggregations
        ? aggregations?.reduce<Record<string, AggregationRoot>>((acc, cur) => {
            if (cur.code !== 'color') {
              return acc;
            }
            acc[cur.option_id] = cur;

            return acc;
          }, {})
        : {},
    [aggregations],
  );
  const [disableBodyScroll, setDisableBodyScroll] = useState(false);
  const [slidesLessThanViewport, setSlidesLessThanViewport] = useState(false);

  const generateSelectAllNextFilterState = (
    prevIsSelectAll: boolean,
    subColors: {
      label: string;
      rewrite: string;
      value: string;
    }[],
  ) => {
    const prevUrlRewriteMap = urlColorRewrites.reduce<Record<string, 1>>((acc, cur) => {
      acc[cur] = 1;
      return acc;
    }, {});
    const nextSubColorRewriteMap = subColors.reduce<Record<string, 1>>((acc, cur) => {
      acc[cur.rewrite] = 1;
      return acc;
    }, {});

    if (prevStateIndex === -1) {
      return filterState.concat({
        attributeCode: 'custom_option_color_ids',
        options: subColors,
      });
    }

    const prevFilterOptions = filterState[prevStateIndex]?.options || [];
    let nextFilterOptions = prevFilterOptions;

    if (prevIsSelectAll) {
      nextFilterOptions = prevFilterOptions.filter(x => {
        return nextSubColorRewriteMap[x.rewrite] !== 1;
      });
    } else {
      nextFilterOptions = prevFilterOptions.concat(subColors.filter(x => prevUrlRewriteMap[x.rewrite] !== 1));
    }

    return nextFilterOptions.length > 0
      ? filterState.map((x, i) => {
          return i === prevStateIndex ? { ...x, options: nextFilterOptions } : x;
        })
      : filterState.filter((_, i) => i !== prevStateIndex);
  };

  const generateSubColorNextFilterState = ({
    label,
    rewrite,
    value,
    prevIsActive,
  }: {
    label: string;
    rewrite: string;
    value: string;
    prevIsActive: boolean;
  }) => {
    if (prevStateIndex === -1) {
      return filterState.concat({
        attributeCode: 'custom_option_color_ids',
        options: [
          {
            label,
            rewrite,
            value,
          },
        ],
      });
    }

    const prevFilterOptions = filterState[prevStateIndex].options || [];
    const nextFilterOptions = prevIsActive
      ? prevFilterOptions.filter(x => x.value !== value)
      : prevFilterOptions.concat({ label, value, rewrite });
    return nextFilterOptions.length > 0
      ? filterState.map((x, i) => {
          return i === prevStateIndex ? { ...x, options: nextFilterOptions } : x;
        })
      : filterState.filter((_, i) => i !== prevStateIndex);
  };

  const generateSubColorLink = (param: { label: string; rewrite: string; value: string; prevIsActive: boolean }) => {
    return generateLinkHref({
      categoryUrlKey,
      filterState: generateSubColorNextFilterState(param),
      isSearchPage: false,
      hideColorsFamily: false,
      colorsFamilyOptions,
      optionIdMapToRewrite,
    });
  };
  const generateSelectAllLink = (
    prevIsSelectAll: boolean,
    subColors: {
      label: string;
      rewrite: string;
      value: string;
    }[],
  ) => {
    return generateLinkHref({
      categoryUrlKey,
      filterState: generateSelectAllNextFilterState(prevIsSelectAll, subColors),
      isSearchPage: false,
      hideColorsFamily: false,
      colorsFamilyOptions,
      optionIdMapToRewrite,
    });
  };

  const firstSelectedColorsFamilyIndex = colorsFamilyOptions?.findIndex(x => {
    return x?.value?.split(',').some(x => {
      const rewrite = colorValueMapToAggregation[x]?.rewrite;

      return urlColorRewrites.includes(rewrite);
    });
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [showScreenMask, setShowScreenMask] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [popoverVisibleIndex, setPopoverVisibleIndex] = useState(-1);

  const handleTogglePopover = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, index: number) => {
    const isOpen = popoverVisibleIndex !== index;

    if (isOpen) {
      scrollToCenter(e.target);
      disableDocumentScroll();
      setPopoverVisibleIndex(index);
    } else {
      setPopoverVisibleIndex(-1);
    }
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    if (isMobile && firstSelectedColorsFamilyIndex >= 0 && scrollContainer) {
      const containerWidth = scrollContainer.offsetWidth;
      const targetPosition = firstSelectedColorsFamilyIndex * 60 + Math.max(0, firstSelectedColorsFamilyIndex - 1) * 10;
      const centerPosition = targetPosition + 60 / 2 - containerWidth / 2 + 20;

      scrollContainer.scrollTo({
        left: centerPosition,
        behavior: 'smooth',
      });
    }
  }, [isMobile, firstSelectedColorsFamilyIndex]);

  const scrollToCenter = element => {
    const container = scrollContainerRef.current;

    if (container && element && element instanceof HTMLDivElement) {
      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const scrollLeft = container.scrollLeft;
      const elementOffsetLeft = elementRect.left - containerRect.left;
      const centerPosition = elementOffsetLeft + elementRect.width / 2 - containerRect.width / 2;

      container.scrollTo({
        left: scrollLeft + centerPosition,
        behavior: 'smooth',
      });
    }
  };

  const handlePopoverOpenChange = (nextOpen: boolean, index: number) => {
    setPopoverVisibleIndex(nextOpen ? index : -1);
  };

  const disableDocumentScroll = () => {
    const htmlElement = document.documentElement;
    setShowScreenMask(true);

    htmlElement.scrollTo({
      left: 0,
      top: 0,
      behavior: 'smooth',
    });
    setDisableBodyScroll(true);

    document.addEventListener(
      'mousedown',
      e => {
        setDisableBodyScroll(false);
        setShowScreenMask(false);

        if (!(e.target instanceof Element && e.target.closest('div[data-m-colors-family="1"]'))) {
          setPopoverVisibleIndex(-1);
        }
      },
      {
        once: true,
      },
    );
  };

  const handleScreenMaskMousedown = () => {
    setShowScreenMask(false);
    setPopoverVisibleIndex(-1);
  };

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    const initHandler = () => {
      const { containerRect, slideRects } = carouselApi.internalEngine() || {};
      const lastSlideRect = slideRects ? [...slideRects].pop() : null;
      const slidesLessThanViewport = !!containerRect && !!lastSlideRect && containerRect.right > lastSlideRect.right;
      setCanScrollPrev(carouselApi.canScrollPrev());
      setCanScrollNext(carouselApi.canScrollNext());
      setSlidesLessThanViewport(slidesLessThanViewport);
    };

    const onSelect = () => {
      setCanScrollPrev(carouselApi.canScrollPrev());
      setCanScrollNext(carouselApi.canScrollNext());
    };

    initHandler();
    onSelect();

    carouselApi.on('reInit', initHandler);
    carouselApi.on('select', onSelect);

    return () => {
      carouselApi.off('select', onSelect);
    };
  }, [carouselApi]);

  const renderColorsFamilyNodes = (inCarousel: boolean = false) => {
    return colorsFamilyOptions?.map((colorsFamily, index) => {
      if (!colorsFamily) {
        return null;
      }

      const values = colorsFamily.value?.split(',');
      const subColorOptions = values?.reduce<AggregationRoot[]>((acc, cur) => {
        const aggregationValue = colorValueMapToAggregation[cur];

        if (aggregationValue && aggregationValue.label && aggregationValue.option_id && aggregationValue.rewrite) {
          acc.push(aggregationValue);
        }

        return acc;
      }, []);

      if (!Array.isArray(subColorOptions) || subColorOptions.length === 0) {
        return null;
      }

      if (isMobile) {
        return (
          <div
            className='w-[60px] flex-none px-0.5'
            key={index}
            data-m-colors-family='1'
          >
            <ColorsFamilyItem
              swatchData={colorsFamily.swatch_data || null}
              colorsFamilyLabel={colorsFamily.label || ''}
              subColorOptions={subColorOptions}
              urlColorRewrites={urlColorRewrites}
              generateSubColorLink={generateSubColorLink}
              generateSelectAllLink={generateSelectAllLink}
              onTogglePopover={e => {
                handleTogglePopover(e, index);
              }}
              popoverVisible={popoverVisibleIndex === index}
              onPopoverOpenChange={(nextOpen: boolean) => {
                handlePopoverOpenChange(nextOpen, index);
              }}
            />
          </div>
        );
      }

      if (inCarousel) {
        return (
          <CarouselItem
            key={index}
            className='basis-[95px] cursor-pointer pl-4 pr-px '
          >
            <ColorsFamilyItem
              swatchData={colorsFamily.swatch_data || null}
              colorsFamilyLabel={colorsFamily.label || ''}
              subColorOptions={subColorOptions}
              urlColorRewrites={urlColorRewrites}
              generateSubColorLink={generateSubColorLink}
              generateSelectAllLink={generateSelectAllLink}
              onTogglePopover={e => {
                handleTogglePopover(e, index);
              }}
              onPopoverOpenChange={(nextOpen: boolean) => {
                handlePopoverOpenChange(nextOpen, index);
              }}
              popoverVisible={popoverVisibleIndex === index}
            />
          </CarouselItem>
        );
      }

      return (
        <ColorsFamilyItem
          key={index}
          swatchData={colorsFamily.swatch_data || null}
          colorsFamilyLabel={colorsFamily.label || ''}
          subColorOptions={subColorOptions}
          urlColorRewrites={urlColorRewrites}
          generateSubColorLink={generateSubColorLink}
          generateSelectAllLink={generateSelectAllLink}
          onTogglePopover={e => {
            handleTogglePopover(e, index);
          }}
          onPopoverOpenChange={(nextOpen: boolean) => {
            handlePopoverOpenChange(nextOpen, index);
          }}
          popoverVisible={popoverVisibleIndex === index}
        />
      );
    });
  };

  return isMobile ? (
    <RemoveScroll enabled={disableBodyScroll}>
      <div className={cn('overflow-hidden', className)}>
        <div
          className=' -mb-5 overflow-auto pb-5 pl-2.5'
          ref={scrollContainerRef}
        >
          <div className='flex items-start gap-x-2.5'>{renderColorsFamilyNodes()}</div>
        </div>
      </div>
      {showScreenMask ? (
        <>
          <div className='relative z-[60]'>
            <div
              onMouseDown={handleScreenMaskMousedown}
              className='absolute top-[11px] h-screen w-full bg-#403732/50'
            />
          </div>
        </>
      ) : null}
    </RemoveScroll>
  ) : (
    <div className={cn('relative mx-auto max-w-site  px-1.5', className)}>
      <div className={clsx('flex items-center gap-x-[15px]')}>
        <CarouselPrevBtn
          onClick={() => {
            carouselApi?.scrollPrev();
          }}
          className={clsx(!canScrollPrev && 'invisible', ' flex h-[66px] w-10 items-center justify-center')}
        />
        <div className='flex-1 overflow-hidden'>
          <Carousel
            className='w-full'
            opts={{
              align: 'start',
            }}
            setApi={setCarouselApi}
          >
            <CarouselContent
              className={clsx('-ml-[15px] flex', slidesLessThanViewport ? 'justify-center' : 'justify-start')}
            >
              {renderColorsFamilyNodes(true)}
            </CarouselContent>
          </Carousel>
        </div>
        <CarouselNextBtn
          onClick={() => {
            carouselApi?.scrollNext();
          }}
          className={clsx(!canScrollNext && 'invisible', ' flex h-[66px] w-10 items-center justify-center')}
        />
      </div>
    </div>
  );
};

const CarouselPrevBtn = (props: React.HtmlHTMLAttributes<HTMLButtonElement>) => {
  return (
    <button
      type='button'
      {...props}
    >
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='13'
        height='22'
        fill='none'
        viewBox='0 0 13 22'
      >
        <path
          stroke='#80583F'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth='2'
          d='m11.115 1-9.23 10 9.23 10'
        />
      </svg>
    </button>
  );
};

const CarouselNextBtn = (props: React.HtmlHTMLAttributes<HTMLButtonElement>) => {
  return (
    <button
      type='button'
      {...props}
    >
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='13'
        height='22'
        fill='none'
        viewBox='0 0 13 22'
      >
        <path
          stroke='#80583F'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth='2'
          d='m1.885 21 9.23-10-9.23-10'
        />
      </svg>
    </button>
  );
};

export default ColorsFamilyFilter;
